import React, { useState, useEffect, useRef, TouchEvent } from "react";
import { useTranslation } from "react-i18next";

// Interface for image data
export interface ImageData {
  id: string;
  url: string;
  width?: number;
  height?: number;
  created_at?: string;
}

// Image Viewer Modal Component props
interface ImageViewerProps {
  image: ImageData | null;
  images?: ImageData[];
  onClose: () => void;
  onNavigate?: (targetId: string) => void;
  showNavigation?: boolean;
  onDelete?: (imageId: string) => void;
  allowDelete?: boolean;
  deleteLoading?: boolean;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  image,
  images = [],
  onClose,
  onNavigate,
  showNavigation = true,
  onDelete,
  allowDelete = false,
  deleteLoading = false,
}) => {
  const { t } = useTranslation();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [exitAnimation, setExitAnimation] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const minSwipeDistance = 50; // Minimum distance for a swipe to register
  const swipeSensitivity = 1.5; // Multiplier for swipe movement
  const imageRef = useRef<HTMLImageElement>(null);

  // Handle Escape key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, showDeleteConfirm]);

  // Add CSS for animations to document head
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes slide-left {
        from { transform: translateX(0); }
        to { transform: translateX(-100%); }
      }
      
      @keyframes slide-right {
        from { transform: translateX(0); }
        to { transform: translateX(100%); }
      }
      
      .animate-slide-left {
        animation: slide-left 0.2s forwards;
      }
      
      .animate-slide-right {
        animation: slide-right 0.2s forwards;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (!image) return null;

  // Handle click outside the image to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (showDeleteConfirm) {
        setShowDeleteConfirm(false);
      } else {
        onClose();
      }
    }
  };

  // Handle delete button click
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete && image) {
      onDelete(image.id);
    }
    setShowDeleteConfirm(false);
    onClose();
  };

  // Cancel delete
  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  // Touch handlers for swipe navigation
  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (!onNavigate || !showNavigation || showDeleteConfirm) return;
    setTouchStart(e.targetTouches[0].clientX);
    setIsNavigating(true);
    setCurrentTranslate(0);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStart || !onNavigate || !showNavigation || showDeleteConfirm)
      return;

    const currentTouch = e.targetTouches[0].clientX;
    const diff = currentTouch - touchStart;

    // Update current translation for visual feedback during swipe
    setCurrentTranslate(diff * swipeSensitivity); // Apply sensitivity
  };

  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStart || !onNavigate || !showNavigation || showDeleteConfirm)
      return;

    const distance = e.changedTouches[0].clientX - touchStart;
    const currentIdx = images.findIndex((img) => img.id === image.id);

    if (Math.abs(distance) >= minSwipeDistance) {
      // Determine direction of swipe
      if (distance > 0 && currentIdx > 0) {
        // Right swipe - go to previous image
        setExitAnimation("slide-right");
        setTimeout(() => {
          onNavigate(images[currentIdx - 1].id);
          setExitAnimation(null);
        }, 200);
      } else if (distance < 0 && currentIdx < images.length - 1) {
        // Left swipe - go to next image
        setExitAnimation("slide-left");
        setTimeout(() => {
          onNavigate(images[currentIdx + 1].id);
          setExitAnimation(null);
        }, 200);
      }
    } else {
      // Reset if swipe wasn't long enough
      setCurrentTranslate(0);
    }

    setIsNavigating(false);
    setTouchStart(null);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDeleteConfirm) return;

    if (!onNavigate || !showNavigation) return;
    const currentIdx = images.findIndex((img) => img.id === image.id);

    if (e.key === "ArrowLeft" && currentIdx > 0) {
      onNavigate(images[currentIdx - 1].id);
    } else if (e.key === "ArrowRight" && currentIdx < images.length - 1) {
      onNavigate(images[currentIdx + 1].id);
    }
  };

  // Get swipe animation classes
  const getAnimationClass = () => {
    if (!showNavigation) return "";

    if (exitAnimation === "slide-left") {
      return "animate-slide-left";
    } else if (exitAnimation === "slide-right") {
      return "animate-slide-right";
    }
    return "";
  };

  // Determine if next or previous navigation buttons should be visible
  const currentIndex = images.findIndex((img) => img.id === image.id);
  const hasPrevious = showNavigation && onNavigate && currentIndex > 0;
  const hasNext =
    showNavigation && onNavigate && currentIndex < images.length - 1;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex justify-center items-center touch-none"
      onClick={handleBackdropClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex={0} // Makes the div focusable for keyboard events
    >
      {showDeleteConfirm ? (
        <div className="bg-gray-900 rounded-lg p-6 max-w-sm mx-auto text-white">
          <h3 className="text-xl font-bold mb-4">
            {t("userImagesPage.deleteConfirmation.title")}
          </h3>
          <p className="mb-6">
            {t("userImagesPage.deleteConfirmation.message")}
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50"
              disabled={deleteLoading}
            >
              {t("userImagesPage.deleteConfirmation.cancelButton")}
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={deleteLoading}
            >
              {deleteLoading
                ? t("userImagesPage.deleteConfirmation.confirmLoadingButton")
                : t("userImagesPage.deleteConfirmation.confirmButton")}
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`relative flex justify-center items-center h-full w-full`}
        >
          <img
            ref={imageRef}
            src={image.url}
            alt={`Full view of image ${image.id}`}
            className={`max-w-[100vw] max-h-[100vh] object-contain transform transition-transform duration-200 ${getAnimationClass()}`}
            style={{
              transform: isNavigating
                ? `translateX(${currentTranslate}px)`
                : "translateX(0)",
            }}
          />

          {/* Navigation buttons for larger screens */}
          {showNavigation && (
            <div className="hidden md:block">
              {hasPrevious && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasPrevious) {
                      onNavigate!(images[currentIndex - 1].id);
                    }
                  }}
                  className="absolute top-1/2 left-4 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
                  aria-label="Previous image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}

              {hasNext && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasNext) {
                      onNavigate!(images[currentIndex + 1].id);
                    }
                  }}
                  className="absolute top-1/2 right-4 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-colors"
                  aria-label="Next image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Swipe indicators for mobile */}
          {showNavigation && (
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 md:hidden pointer-events-none">
              {hasPrevious && (
                <div className="text-white bg-black bg-opacity-50 rounded-full p-1 opacity-50">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </div>
              )}

              {hasNext && (
                <div className="text-white bg-black bg-opacity-50 rounded-full p-1 opacity-50">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              )}
            </div>
          )}

          {/* Control buttons */}
          <div className="absolute top-2 right-2 flex space-x-2">
            {allowDelete && onDelete && (
              <button
                onClick={handleDeleteClick}
                className="bg-red-600 bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-100 transition-colors z-10"
                aria-label="Delete image"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="bg-gray-800 bg-opacity-70 text-white p-2 rounded-full hover:bg-gray-700 transition-colors z-10"
              aria-label="Close image viewer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Mini Gallery */}
          {showNavigation && images.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 overflow-x-auto py-2 px-4 whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex space-x-2 justify-center">
                {images.map((thumbImage) => (
                  <img
                    key={thumbImage.id}
                    src={thumbImage.url} // Consider using dedicated thumbnail URLs if available
                    alt={`Thumbnail ${thumbImage.id}`}
                    className={`h-12 w-auto object-cover rounded cursor-pointer border-2 ${
                      image.id === thumbImage.id
                        ? "border-blue-500"
                        : "border-transparent hover:border-gray-500"
                    } transition-all duration-150`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent backdrop click
                      // Find the clicked image in the main array to navigate
                      const targetImage = images.find(
                        (img) => img.id === thumbImage.id
                      );
                      if (
                        targetImage &&
                        onNavigate &&
                        image.id !== targetImage.id
                      ) {
                        // Navigate directly to the clicked image ID
                        onNavigate(targetImage.id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
